import * as luxon from 'luxon';
import { getIntl } from './intl';
import getAge from './getAge';
import { Dynamo } from '../primary-record-types';

const intl = getIntl('getDateString', {
    endsOn: 'ends on {date}',
    startsOn: 'starts on {date}',
    startsOnEndsOnSameDayTimeFormat: 'starts on {startDate} and ends at {endDate}',
    startsOnEndsOnSameDayFormat: 'starts and ends on {startDate}',
    startsOnEndsOnDifferentDay: 'starts on {startDate} and ends on {endDate}',
    ageString: '{date} ({age})',
    browserOsFallback: `{browserName} on {osName} device`,
    browserName: `{browserName} browser`,
    osName: `{osName} operating system`,
});

export const DATE_LUXON_FORMAT = 'MM/dd/yyyy';
export const TIME_LUXON_FORMAT = 'h:mm a';
export const TIME_WITH_SECONDS_LUXON_FORMAT = 'h:mm:ss a';

const getLuxonDate = (rawDate: string | number) => {
    return typeof rawDate === 'number' ? luxon.DateTime.fromJSDate(new Date(rawDate)) : luxon.DateTime.fromISO(rawDate);
};

export default function getDateString(
    rawDate: string | number | undefined | null,
    format: 'date' | 'date-time' | 'date-time-seconds' | 'time' | 'time-seconds' | 'month-year',
): string {
    if (!rawDate) {
        return '';
    }

    const luxonDate = getLuxonDate(rawDate);
    if (!luxonDate.isValid) {
        return '';
    }

    if (format === 'date') {
        return luxonDate.toFormat(DATE_LUXON_FORMAT);
    } else if (format === 'date-time') {
        return luxonDate.toFormat(`${DATE_LUXON_FORMAT} ${TIME_LUXON_FORMAT}`);
    } else if (format === 'date-time-seconds') {
        return luxonDate.toFormat(`${DATE_LUXON_FORMAT} ${TIME_WITH_SECONDS_LUXON_FORMAT}`);
    } else if (format === 'month-year') {
        return luxonDate.toFormat('MM/yy');
    } else if (format === 'time-seconds') {
        return luxonDate.toFormat(TIME_WITH_SECONDS_LUXON_FORMAT);
    } else {
        return luxonDate.toFormat(TIME_LUXON_FORMAT);
    }
}

export function getMinimalDateTimeSpan(startDate: string | undefined, endDate: string | undefined) {
    if (!startDate) return '';

    if (!endDate) return getDateString(startDate, 'date-time');

    const isSameTime = luxon.DateTime.fromISO(startDate).hasSame(luxon.DateTime.fromISO(endDate), 'minute');
    if (isSameTime) return getDateString(startDate, 'date-time');

    const isSameDay = luxon.DateTime.fromISO(startDate).hasSame(luxon.DateTime.fromISO(endDate), 'day');
    const endDateFormatted = isSameDay ? getDateString(endDate, 'time') : getDateString(endDate, 'date-time');
    return `${getDateString(startDate, 'date-time')} - ${endDateFormatted}`;
}

export function getDateSpanString(
    startDate: string | undefined,
    endDate: string | undefined,
    format: 'date' | 'date-time' | 'date-time-seconds',
) {
    if (!startDate && !endDate) {
        return '';
    }

    if (startDate && !endDate) {
        const formatted = getDateString(startDate, format);
        return intl('startsOn', { date: formatted });
    } else if (!startDate && endDate) {
        const formatted = getDateString(endDate, format);
        return intl('endsOn', { date: formatted });
    } else if (startDate && endDate) {
        const isSameDay = luxon.DateTime.fromISO(startDate).hasSame(luxon.DateTime.fromISO(endDate), 'day');
        if (isSameDay && (format === 'date-time' || format === 'date-time-seconds')) {
            const startDateFormatted = getDateString(startDate, format);
            const timeFormat = format === 'date-time-seconds' ? TIME_WITH_SECONDS_LUXON_FORMAT : TIME_LUXON_FORMAT;
            const endTimeFormatted = luxon.DateTime.fromISO(endDate).toFormat(timeFormat);
            return intl('startsOnEndsOnSameDayTimeFormat', {
                startDate: startDateFormatted,
                endDate: endTimeFormatted,
            });
        } else if (isSameDay && format === 'date') {
            const startDateFormatted = getDateString(startDate, format);
            return intl('startsOnEndsOnSameDayFormat', {
                startDate: startDateFormatted,
            });
        } else {
            const startDateFormatted = getDateString(startDate, format);
            const endDateFormatted = getDateString(endDate, format);
            return intl('startsOnEndsOnDifferentDay', {
                startDate: startDateFormatted,
                endDate: endDateFormatted,
            });
        }
    }
}

export function getAgeString(rawDate: string | number) {
    const luxonDate = getLuxonDate(rawDate);
    if (!luxonDate.isValid) return '';

    const date = getDateString(luxonDate.toISO(), 'date');
    const age = getAge(luxonDate.toISO());
    return intl('ageString', { date, age: String(age) });
}

export function getIpLocationDescription(ipLocation: Dynamo.IPLocation | null) {
    if (!ipLocation) return '';

    const { city, region, country_name } = ipLocation;
    return [city, region, country_name].filter(Boolean).join(', ');
}

export function getParsedUserAgentDescription(parsedUserAgent: Dynamo.ParsedUserAgent | null) {
    if (!parsedUserAgent) return '';

    const { vendor, model, type } = parsedUserAgent;
    if (vendor || model) {
        return [vendor, model, type].filter(Boolean).join(' ');
    } else if (parsedUserAgent.osName && parsedUserAgent.browserName) {
        return intl('browserOsFallback', {
            browserName: parsedUserAgent.browserName,
            osName: parsedUserAgent.osName,
        });
    } else if (parsedUserAgent.osName) {
        return intl('osName', { osName: parsedUserAgent.osName });
    } else if (parsedUserAgent.browserName) {
        return intl('browserName', { browserName: parsedUserAgent.browserName });
    } else {
        return '';
    }
}
